import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usePublicSurvey, useSubmitSurvey } from '../../hooks/useSurveys';
import type { SurveyQuestion } from '../../types';
import { useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const answerSchema = z.object({
  answers: z.array(
    z.object({
      question_id: z.string(),
      answer_value: z.union([z.string(), z.number(), z.array(z.string())]),
    }),
  ),
});

type SurveyFormData = z.infer<typeof answerSchema>;

export default function SurveyForm() {
  const { token } = useParams<{ token: string }>();
  const { data: surveyData, isLoading, error } = usePublicSurvey(token || '');
  const submitSurvey = useSubmitSurvey(token || '');
  const [submitted, setSubmitted] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SurveyFormData>({
    resolver: zodResolver(answerSchema),
    defaultValues: {
      answers: [],
    },
  });

  const onSubmit = async (data: SurveyFormData) => {
    // Filter out empty answers and ensure all required ones are filled
    const filledAnswers = data.answers.filter(
      (a) => a.answer_value !== '' && a.answer_value !== 0 && (!Array.isArray(a.answer_value) || a.answer_value.length > 0),
    );

    // Validate required questions
    const requiredQuestions = surveyData?.questions?.filter((q) => q.is_required) || [];
    const missingRequired = requiredQuestions.filter(
      (q) => !filledAnswers.find((a) => a.question_id === q.id),
    );

    if (missingRequired.length > 0) {
      alert('Please answer all required questions.');
      return;
    }

    try {
      await submitSurvey.mutateAsync({ answers: filledAnswers });
      setSubmitted(true);
    } catch {
      // Error shown in UI
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  // Error state
  if (error || !surveyData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Survey Not Available</h1>
          <p className="text-gray-500">
            This survey link may be invalid or expired. Please contact the sender.
          </p>
        </div>
      </div>
    );
  }

  // Already completed
  if (surveyData.is_completed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Already Completed</h1>
          <p className="text-gray-500">
            You have already submitted your response for this survey. Thank you!
          </p>
        </div>
      </div>
    );
  }

  // Expired
  if (surveyData.is_expired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Survey Expired</h1>
          <p className="text-gray-500">
            This survey has expired and is no longer accepting responses.
          </p>
        </div>
      </div>
    );
  }

  // Submitted successfully
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-500">
            Your response has been recorded. We appreciate your feedback!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* Survey Header */}
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900">{surveyData.template_name}</h1>
            <p className="mt-2 text-sm text-gray-500">
              Please take a moment to share your feedback with us.
            </p>
          </div>

          {/* Survey Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {(surveyData.questions ?? [])
              .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
              .map((question, index) => (
                <QuestionField
                  key={question.id}
                  question={question}
                  index={index}
                  control={control}
                />
              ))}

            {errors.answers && (
              <p className="text-sm text-red-600">Please complete all required questions.</p>
            )}

            {submitSurvey.isError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  Failed to submit your response. Please try again.
                </p>
              </div>
            )}

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitSurvey.isPending}
                className="w-full rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
              >
                {submitSurvey.isPending ? 'Submitting...' : 'Submit Response'}
              </button>
            </div>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-gray-400">
          Your responses are confidential and will be used to improve our services.
        </p>
      </div>
    </div>
  );
}

// Individual question component

interface QuestionFieldProps {
  question: SurveyQuestion;
  index: number;
  control: Control<SurveyFormData>;
}

function QuestionField({ question, index, control }: QuestionFieldProps) {
  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-900">
        {index + 1}. {question.question_text}
        {question.is_required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <Controller
        control={control}
        name={`answers.${index}`}
        defaultValue={{ question_id: question.id, answer_value: '' }}
        render={({ field }) => (
          <QuestionInput
            question={question}
            value={field.value?.answer_value ?? ''}
            onChange={(val) => field.onChange({ question_id: question.id, answer_value: val })}
          />
        )}
      />
    </div>
  );
}

interface QuestionInputProps {
  question: SurveyQuestion;
  value: string | number | string[];
  onChange: (value: string | number | string[]) => void;
}

function QuestionInput({ question, value, onChange }: QuestionInputProps) {
  switch (question.question_type) {
    case 'csat_score':
    case 'scale':
      return <ScaleInput value={value as number} onChange={onChange} max={5} labels={['Very Dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied']} />;

    case 'rating':
    case 'star':
      return <StarInput value={value as number} onChange={onChange} />;

    case 'nps_score':
      return <ScaleInput value={value as number} onChange={onChange} max={10} />;

    case 'yes_no':
      return (
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => onChange('yes')}
            className={`px-6 py-2 rounded-lg border text-sm font-medium transition-colors ${
              value === 'yes'
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
            }`}
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => onChange('no')}
            className={`px-6 py-2 rounded-lg border text-sm font-medium transition-colors ${
              value === 'no'
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
            }`}
          >
            No
          </button>
        </div>
      );

    case 'text':
      return (
        <textarea
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="Enter your response..."
        />
      );

    case 'single_choice':
      return (
        <div className="space-y-2">
          {question.options?.map((option) => (
            <label
              key={option}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                value === option
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name={`question-${question.id}`}
                value={option}
                checked={value === option}
                onChange={() => onChange(option)}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-900">{option}</span>
            </label>
          ))}
        </div>
      );

    case 'multi_choice':
      return <MultiChoiceInput question={question} value={value as string[]} onChange={onChange} />;

    default:
      return null;
  }
}

// Scale input (1 to max)
function ScaleInput({
  value,
  onChange,
  max,
  labels,
}: {
  value: number;
  onChange: (val: number) => void;
  max: number;
  labels?: string[];
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {Array.from({ length: max }, (_, i) => i + 1).map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => onChange(num)}
            className={`h-10 w-10 rounded-lg border text-sm font-medium transition-colors ${
              value === num
                ? 'border-indigo-500 bg-indigo-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-300'
            }`}
          >
            {num}
          </button>
        ))}
      </div>
      {labels && (
        <div className="flex justify-between text-xs text-gray-500">
          <span>{labels[0]}</span>
          <span>{labels[labels.length - 1]}</span>
        </div>
      )}
    </div>
  );
}

// Star rating input
function StarInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (val: number) => void;
}) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="p-0.5"
          aria-label={`${star} star${star > 1 ? 's' : ''}`}
        >
          <svg
            className={`h-8 w-8 transition-colors ${
              star <= (hovered || (value as number))
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
            />
          </svg>
        </button>
      ))}
      {(value as number) > 0 && (
        <span className="ml-2 text-sm text-gray-500">{value}/5</span>
      )}
    </div>
  );
}

// Multi-choice input
function MultiChoiceInput({
  question,
  value,
  onChange,
}: {
  question: SurveyQuestion;
  value: string[];
  onChange: (val: string[]) => void;
}) {
  const selectedValues = Array.isArray(value) ? value : [];

  const toggleOption = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter((v) => v !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  return (
    <div className="space-y-2">
      {question.options?.map((option) => (
        <label
          key={option}
          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
            selectedValues.includes(option)
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <input
            type="checkbox"
            checked={selectedValues.includes(option)}
            onChange={() => toggleOption(option)}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-900">{option}</span>
        </label>
      ))}
    </div>
  );
}
